/* eslint-disable react/jsx-indent */
/* eslint-disable react/jsx-curly-newline */
import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMixpanel } from 'react-mixpanel-browser';
import { useDispatch, useSelector } from 'react-redux';

import { State } from 'redux/initialState';
import menuTypes from 'constants/menuTypes';
import sizes from 'constants/sizes';

import { useTranslate } from 'hooks/useTranslate';
import { useGetActualSociety } from 'hooks/useGetActualSociety';

import Page from 'modules/_shared/components/Pages/Page';
import PageListHeader from 'modules/_shared/components/Headers/PageListHeader';
import UploadPartner from 'modules/_shared/components/Modals/UploadPartner';
import SharesTransferExcelModal from 'modules/_shared/components/Modals/SharesTransferExcel';
import { SharesTransferExcelModalProvider } from 'modules/_shared/components/Modals/SharesTransferExcel/context/SharesTransferExcelModalContext';
import EncumbrancesExcelModalWithProvider from 'modules/_shared/components/Modals/EncumbrancesExcel/EncumbrancesExcelModalWithProvider';
import EmptyStateCard from 'modules/_shared/components/EmptyStates/EmptyStateCard';
import CustomDataTable from 'modules/_shared/components/Tables/CustomDataTable';
import OperationMenuDots from 'modules/_shared/components/MenuDots/OperationMenuDots';
import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetPartners } from 'modules/partners/hooks/useGetPartners';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { useGetOperationsMenu } from 'modules/transactions/hooks/useGetOperationsMenu';
import { useGetPartnersSectionPermissions } from 'modules/partners/hooks/useGetPartnerSectionsPermissions';
import {
  hasPaginationChanged,
  mapPaginationToPaginationData,
} from 'modules/_shared/components/Pagination/paginationUtils';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import DownloadTableModal from 'modules/_shared/components/Modals/DownloadTableModal';
import { DownloadTableModalProvider } from 'modules/_shared/components/Modals/DownloadTableModal/context/DownloadTableModalContext';

import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';
import isSuperAdmin from 'utils/isSuperAdmin';
import { usePartnerList } from './hooks/usePartnerList';
import { getCategoryLabel } from './utils/getCategoryLabel';
import { getPageDescription } from './utils/getPageDescription';
import { getDefaultPartnerListSort } from './utils/getDefaultPartnerListSort';
import {
  COLUMN_FIELD_TO_SORT,
  DEFAULT_SORT_BY,
  SORT_FIELD_ID_BY_API,
} from './constants/sortFieldMap';

const INITIAL_PAGE = 1;
const INITIAL_ROWS_PER_PAGE = 10;
const initialDefaultSort = getDefaultPartnerListSort();

const PartnerList = () => {
  const { t } = useTranslate();
  const mixpanel = useMixpanel();
  const dispatch = useDispatch();
  const { societyId } = useParams();

  const user = useSelector((state: State) => state.user);
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const partnerCategories = useSelector(
    (state: State) => state.partnerCategories
  );

  const [showNationality, setShowNationality] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [currentPage, setCurrentPage] = useState<number>(INITIAL_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState<number>(INITIAL_ROWS_PER_PAGE);
  const [sortBy, setSortBy] = useState<string>(initialDefaultSort.sortBy);
  const [sortOrder, setSortOrder] = useState<string>(
    initialDefaultSort.sortOrder
  );
  const [hasUserSorted, setHasUserSorted] = useState(false);
  const [paginationData, setPaginationData] = useState({
    customItemPerPage: INITIAL_ROWS_PER_PAGE,
    itemPerPage: INITIAL_ROWS_PER_PAGE,
    currentPage: INITIAL_PAGE,
    totalItems: 0,
    totalPages: 0,
  });

  const { data: operationsMenu } = useGetOperationsMenu();

  const { isAdmin, isReadOnly } = useGetPartnersSectionPermissions().data;

  const searchParams = new URLSearchParams(location.href.split('?')[1]);
  const categoryId = searchParams.get('categoria') || '';

  const {
    data: { tableData, customData, currentTableColumns },
    isParsing,
  } = usePartnerList({
    isAdmin,
    isReadOnly,
    showNationality,
  });

  const { isLoading, isError, pagination } = useGetPartners({
    ...(!isAdmin && !actualSociety?.hasAccessCaptable && { userId: user._id }),
    societyId,
    partnerId: '',
    categoryId,
    page: currentPage,
    items: rowsPerPage,
    searchTerm,
    sortBy,
    sortOrder,
  });

  useGetMenu(menuTypes.EXTENDED);
  useGetActualSociety(societyId);

  const handleChangePage = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsCount: number) => {
    setRowsPerPage(newRowsCount);
  };

  const handleSort = useCallback(
    (column: { field?: string }, direction: 'asc' | 'desc') => {
      const fallbackSortBy = getDefaultPartnerListSort(actualSociety).sortBy;
      const sortField =
        COLUMN_FIELD_TO_SORT[column?.field ?? ''] ?? fallbackSortBy;
      setHasUserSorted(true);
      setSortBy(sortField);
      setSortOrder(direction === 'asc' ? 'asc' : 'desc');
      setCurrentPage(INITIAL_PAGE);
    },
    [actualSociety]
  );

  const sortFieldId =
    SORT_FIELD_ID_BY_API[sortBy] ?? SORT_FIELD_ID_BY_API[DEFAULT_SORT_BY];

  const handleDownloadTable = () => {
    store.dispatch(
      setModal(
        <DownloadTableModalProvider categoryId={categoryId}>
          <DownloadTableModal />
        </DownloadTableModalProvider>
      )
    );
  };

  const handleClickUploadButton = () => {
    dispatch(
      setModal(
        <UploadPartner
          // @ts-ignore
          user={user}
          societyId={actualSociety['_id']}
        />
      )
    );
  };

  const handleClickUploadOperationsButton = () => {
    dispatch(
      setModal(
        <SharesTransferExcelModalProvider size={sizes.XL}>
          <SharesTransferExcelModal />
        </SharesTransferExcelModalProvider>
      )
    );
  };

  const handleClickUploadEncumbrancesButton = () => {
    dispatch(setModal(<EncumbrancesExcelModalWithProvider size={sizes.XL} />));
  };

  const handleClickOperationButton = (Modal: any) => {
    dispatch(setModal(<Modal user={user} societyId={actualSociety._id} />));
  };

  useEffect(() => {
    if (pagination && hasPaginationChanged(pagination, paginationData)) {
      setPaginationData((prevState) => ({
        ...prevState,
        ...mapPaginationToPaginationData(pagination),
      }));
    }
  }, [pagination]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPage(INITIAL_PAGE);
  }, [searchTerm]);

  useEffect(() => {
    setHasUserSorted(false);
  }, [societyId]);

  useEffect(() => {
    if (!actualSociety || hasUserSorted) return;

    const { sortBy: nextSortBy, sortOrder: nextSortOrder } =
      getDefaultPartnerListSort(actualSociety);

    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
  }, [actualSociety, hasUserSorted]);

  return (
    <Page id="partners-list-page">
      <PageListHeader
        subTitle={t('Partners')}
        subTitleSuffix={getCategoryLabel({ categoryId, partnerCategories })}
        description={getPageDescription({ society: actualSociety })}
        actions={[
          isSuperAdmin(user) && (
            <Button
              variant={ButtonVariants.LIGHT}
              icon="upload-cloud"
              text={t('UploadListing')}
              onClick={handleClickUploadButton}
              disabled={isReadOnly}
              outlineStyle
            />
          ),
          isSuperAdmin(user) && (
            <Button
              variant={ButtonVariants.LIGHT}
              icon="file-xls"
              text={t('UploadOperations')}
              onClick={handleClickUploadOperationsButton}
              disabled={isReadOnly}
              outlineStyle
            />
          ),
          isSuperAdmin(user) && (
            <Button
              variant={ButtonVariants.LIGHT}
              icon="file-xls"
              text={t('UploadEncumbrances')}
              onClick={handleClickUploadEncumbrancesButton}
              disabled={isReadOnly}
              outlineStyle
              data-testid="upload-encumbrances-button"
            />
          ),
          isAdmin && (
            <OperationMenuDots
              menuOptions={operationsMenu}
              handleModal={handleClickOperationButton}
              params={{
                society: actualSociety,
                user,
                mixpanel,
              }}
              disabled={isReadOnly}
            />
          ),
        ].filter(Boolean)}
      />

      <CustomDataTable
        data={tableData}
        columns={currentTableColumns}
        searchBy={[t('Partner'), t('Email'), t('CIF')]}
        searchByPlaceholder={[t('name'), t('email'), t('CIF')].join()}
        defaultSortFieldId={sortFieldId}
        defaultSortAsc={sortOrder === 'asc'}
        sortServer
        onSort={handleSort}
        pagination
        className="nk-tb-list"
        expandableRows={false}
        actions={isAdmin}
        showDense
        showDownload={isAdmin}
        showNationality={isAdmin}
        toggleNationality={() =>
          isAdmin ? setShowNationality(!showNationality) : undefined
        }
        customData={customData}
        filterByParam={isAdmin ? 'isActive' : undefined}
        filterText={
          isAdmin ? { true: t('SeeOld'), false: t('HideOld') } : undefined
        }
        filterIcon={
          isAdmin ? { true: 'ni ni-eye', false: 'ni ni-eye-off' } : undefined
        }
        onFilterChange={setSearchTerm}
        fileName={`${t('PartnersList')} ${actualSociety?.name || ''}`}
        isLoading={isLoading}
        // PAGINATION
        hasCustomPagination
        customPagination={{
          className: 'bg-white',
          totalItems: paginationData.totalItems,
          itemPerPage: paginationData.itemPerPage,
          currentPage: paginationData.currentPage,
          customItemPerPage: paginationData.customItemPerPage,
          onChangePage: handleChangePage,
          onChangeRowsPerPage: handleChangeRowsPerPage,
          setRowsPerPage,
        }}
        onDownloadTable={handleDownloadTable}
      />

      {(isLoading || isParsing) && <CustomLoading />}

      {isError && !isLoading && !isParsing && (
        <EmptyStateCard
          mainMessage={t('ErrorLoadingPartnersList')}
          infoMessage={t('ErrorLoadingPartnersListMessage')}
          bordered={false}
        />
      )}
    </Page>
  );
};

export default PartnerList;
